// react
import { useDispatch } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';

// api
import { useDeleteEforsyningMetadataRowIntegrationsEforsyningMetadataPartitionKeyRowKeyDeleteMutation } from '@/api/generated/dataIntegrations/endpoints/dataIntegrations';

// components
import { showSnackbar } from '@/redux/snackbarSlice';

interface EforsyningIntegration {
  PartitionKey?: string | number;
  RowKey?: string;
}

interface DeleteEforsyningIntegrationDialogProps {
  open: boolean;
  integration: EforsyningIntegration | null;
  onClose: () => void;
  onDeleted?: (integration: EforsyningIntegration) => void;
}

const DeleteEforsyningIntegrationDialog = ({
  open,
  integration,
  onClose,
  onDeleted,
}: DeleteEforsyningIntegrationDialogProps) => {
  const { t } = useTranslation(['eforsyning-integration', 'common']);
  const dispatch = useDispatch();
  const [deleteIntegration, { isLoading: isDeleting }] =
    useDeleteEforsyningMetadataRowIntegrationsEforsyningMetadataPartitionKeyRowKeyDeleteMutation();

  const handleConfirm = async () => {
    if (integration?.PartitionKey == null || !integration?.RowKey) return;

    const result = await deleteIntegration({
      partitionKey: Number(integration.PartitionKey),
      rowKey: integration.RowKey,
    });

    if (!('error' in result)) {
      dispatch(
        showSnackbar({
          message:
            t('deleteSuccess', {
              defaultValue: 'Metadata row deleted successfully',
            }) || 'Integration deleted successfully',
          severity: 'success',
        }),
      );

      onClose();
      if (integration) {
        onDeleted?.(integration);
      }
    }
    // Errors are handled globally by apiErrorMiddleware (snackbar + App Insights).
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('deleteTitle', {
          defaultValue: 'Delete Integration',
        })}
      </DialogTitle>
      <DialogContent dividers>
        <Typography>
          {t('deleteConfirmation', {
            defaultValue:
              'Are you sure you want to delete this metadata row? This action cannot be undone.',
          })}
        </Typography>
        <Typography sx={{ mt: 2, fontWeight: 'bold' }}>
          {t('username', {
            defaultValue: 'Username',
          })}
          :{' '}
          {integration?.RowKey ||
            t('common.notAvailable', {
              ns: 'common',
              defaultValue: 'Not Available',
            })}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isDeleting}>
          {t('actions.cancel', { ns: 'common', defaultValue: 'Cancel' })}
        </Button>
        <Button
          onClick={handleConfirm}
          color="error"
          variant="contained"
          disabled={isDeleting}
          data-testid="delete-eforsyning-integration-confirm-btn"
        >
          {isDeleting
            ? t('deleting', {
                defaultValue: 'Deleting...',
              })
            : t('actions.delete', { ns: 'common', defaultValue: 'Delete' })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteEforsyningIntegrationDialog;
